import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { useAuth0 } from "@auth0/auth0-react";
import { useTheme } from "next-themes";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { patchSingleCPIcebergSetting } from "@/store/tradeSettingsStoreACTIONS";
import { updateCPReleaseSizeInStore } from "@/store/tradeDataStoreACTIONS";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import agGridFlashNotifications from "@/store/notifications/agGridFlashNotifications";
import { generateToastMessageForTradeFlowErrors } from "@/lib/notifications/toastNotificationHelpers";
import {
  calculateIcebergPercentForER,
  isIcebergReduction,
} from "./icebergHelpers";
import { getCPFromStore } from "../../tradeLookupHelpers";
import { toast } from "react-toastify";
import {
  buildIcebergMinSizeMessage,
  findBlocksUnderIcebergMin,
  getIcebergBlocksForTrade,
} from "./icebergMinSizeHelpers";

function flashIcebergErCell(esid: string, executionRequestId: number) {
  tradeSettingsStore
    .getState()
    .updateIcebergErCellsToFlash([
      { esid, executionRequestId, field: "releaseSize" },
    ]);
  setTimeout(
    () => tradeSettingsStore.getState().clearIcebergErCellsToFlash(),
    agGridFlashNotifications.getState().validationFlashDuration,
  );
}

/**
 * IcebergCellEditor (LQC-922)
 *
 * In-cell editor for the per-CP iceberg percentage on the OP ER row.
 * The user types a percent (0-100); on save we convert to decimal,
 * optimistically write the new releaseSize to tradeDataStore so the cell
 * snaps immediately, then fire PATCH /Trade/Execution/Settings. MegaPipe's
 * follow-up push reconciles the authoritative value through the regular
 * trade-data update path.
 */
const IcebergCellEditor = forwardRef((params: any, ref) => {
  const { theme } = useTheme();
  const { getAccessTokenSilently } = useAuth0();
  const { fullTradeData } = returnAllTradeDetailsFromGenericParams(params);

  const executionRequestId = params.data?.executionRequestId;

  // Captured at mount so validateAndSave can detect a click-in/click-out
  // (no real edit) and skip the PATCH + optimistic store write.
  const initialValueRef = useRef<string>("");

  const [value, setValue] = useState(() => {
    const liveCP = getCPFromStore(fullTradeData?.esid, executionRequestId);
    const initial = calculateIcebergPercentForER(liveCP ?? params.data);
    const initialString =
      initial === null ? "" : Math.round(initial).toString();
    initialValueRef.current = initialString;
    return initialString;
  });

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    if (input === "") {
      setValue(input);
      return;
    }
    if (!/^\d*\.?\d*$/.test(input)) return;
    // if (Number(input) > 100) {
    //   toast.error("Iceberg value must be between 0 and 100");
    //   return;
    // }
    setValue(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      params.stopEditing();
    } else if (e.key === "Escape") {
      params.stopEditing(true);
    }
  };

  const validateAndSave = () => {
    const esid = fullTradeData?.esid;

    if (value === "" || !esid || executionRequestId === undefined) {
      return undefined;
    }

    const numeric = Number(value);
    if (isNaN(numeric)) return undefined;

    // Iceberg is conventionally whole percentages — round before converting
    // to the API's decimal so we never persist fractional iceberg sizes.
    const clamped = Math.round(Math.min(Math.max(numeric, 0), 100));
    const decimal = clamped / 100;

    // No-op when the user opened the editor and closed it without changing
    // the value — avoid an unnecessary PATCH on click-in/click-out.
    // We still return the decimal so AG Grid's row-data write keeps the
    // existing shape; the column field is "releaseSize" (a 0-1 decimal in
    // tradeDataStore), and AG Grid's row references can be the same CP
    // objects living in the store — returning a percent here would clobber
    // the store value (70 → cpHasIceberg false → cell renders empty).
    if (value === initialValueRef.current) {
      return decimal;
    }

    // LQC-1040: post-send, the release size can only increase. Compare against
    // the live CP value (which is what the AG Grid row is currently showing,
    // since the editor only opens in post-send statuses where store === sent).
    const liveCP = getCPFromStore(esid, executionRequestId);
    const currentDecimal = Number(liveCP?.releaseSize);
    if (isIcebergReduction(decimal, currentDecimal)) {
      generateToastMessageForTradeFlowErrors({
        action: "icebergSize",
        outcome: "attemptedReduction",
      });
      flashIcebergErCell(esid, executionRequestId);
      return Number.isFinite(currentDecimal) ? currentDecimal : undefined;
    }

    // LQC-2874: reject an iceberg that would release a block under 100,000.
    const blocks = liveCP?.legs?.length
      ? liveCP.legs
      : getIcebergBlocksForTrade(esid);
    if (findBlocksUnderIcebergMin(blocks, clamped).length) {
      toast.error(buildIcebergMinSizeMessage(blocks));
      flashIcebergErCell(esid, executionRequestId);
      return Number.isFinite(currentDecimal) ? currentDecimal : undefined;
    }

    updateCPReleaseSizeInStore({
      esid,
      executionRequestId,
      releaseSize: decimal,
    });

    patchSingleCPIcebergSetting({
      esid,
      executionRequestId,
      releaseSize: decimal,
      getAccessTokenSilently,
    });

    return decimal;
  };

  useImperativeHandle(ref, () => ({
    getValue: () => validateAndSave(),
  }));

  const editableStyle =
    theme === "light"
      ? "inputCellOtherLight roundedInputCell"
      : "inputCellOtherDark roundedInputCell";

  return (
    <input
      ref={inputRef}
      type="number"
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      className={`ag-roundedInputStyle ag-input text-center ${editableStyle}`}
      data-testid="IcebergCellEditor"
      id="IcebergCellEditor"
    />
  );
});

export default IcebergCellEditor;
